import type { FeatureName, IModelsAPI } from '@wix/thunderbolt-symbols'
import type { IEffectsTriggerApi } from '@wix/thunderbolt-platform-types'
import type { IViewerHandlers } from '../types'
import { EFFECTS_TRIGGER_API, MODELS_API, VIEWER_HANDLERS } from './moduleNames'
import { getFullId } from '@wix/thunderbolt-commons'

const blankApi = {
	getEffects: () => [],
	getActiveEffects: () => [],
	applyEffects: () => {},
	toggleEffects: () => {},
	removeEffects: () => {},
	removeAllEffects: () => {},
}

export const EffectsTriggerApiFactory = (
	modelsApi: IModelsAPI,
	{ viewerHandlers: currentPageViewerHandlers, createViewerHandlers }: IViewerHandlers
): IEffectsTriggerApi => {
	const activeEffectsMap = {} as { [compId: string]: Array<string> }

	const invalidateComponentEffects = (compId: string, effectNames: Array<string>) => {
		const _compId = getFullId(compId)
		const availableEffects = new Set(modelsApi.getEffectsByCompId(_compId))
		return Array.from(new Set(effectNames)).filter((effect) => availableEffects.has(effect))
	}

	const initByNamespace = (namespace: FeatureName) => {
		if (modelsApi.isFeatureEnabledOnMasterPage(namespace)) {
			const masterPageHandlers = createViewerHandlers('masterPage')
			masterPageHandlers[namespace].registerToActiveEffectsChange(
				(compId: string, list: Array<string>) => (activeEffectsMap[compId] = list)
			)
		}
		if (modelsApi.isFeatureEnabledOnPage(namespace)) {
			currentPageViewerHandlers[namespace].registerToActiveEffectsChange(
				(compId: string, list: Array<string>) => (activeEffectsMap[compId] = list)
			)
		}
	}

	const getNamespace = (isInteractionsFeatureDisabled: boolean): FeatureName => {
		return isInteractionsFeatureDisabled ? 'triggersAndReactions' : 'interactions'
	}

	return {
		init() {
			initByNamespace('interactions')
			initByNamespace('triggersAndReactions')
		},

		createCompTriggerAndReactionsApi: (compId: string) => {
			const pageId = modelsApi.getPageIdByCompId(compId)

			const isTriggersAndReactionsFeatureDisabled =
				(pageId === 'masterPage' && !modelsApi.isFeatureEnabledOnMasterPage('triggersAndReactions')) ||
				(pageId !== 'masterPage' && !modelsApi.isFeatureEnabledOnPage('triggersAndReactions'))
			const isInteractionsFeatureDisabled =
				(pageId === 'masterPage' && !modelsApi.isFeatureEnabledOnMasterPage('interactions')) ||
				(pageId !== 'masterPage' && !modelsApi.isFeatureEnabledOnPage('interactions'))

			if (isTriggersAndReactionsFeatureDisabled && isInteractionsFeatureDisabled) {
				return blankApi
			}
			const handlers = createViewerHandlers(pageId)
			const namespace = getNamespace(isInteractionsFeatureDisabled)

			return {
				getEffects: () => modelsApi.getEffectsByCompId(compId),

				getActiveEffects: () => activeEffectsMap[compId] || [],

				applyEffects: (...effectNames: Array<string>) => {
					const effects = invalidateComponentEffects(compId, effectNames)
					activeEffectsMap[compId] = [...(activeEffectsMap[compId] || []), ...effects]
					handlers[namespace].applyEffects(compId, effects)
				},

				removeEffects: (...effectNames: Array<string>) => {
					const effects = invalidateComponentEffects(compId, effectNames)
					activeEffectsMap[compId] = (activeEffectsMap[compId] || []).filter((effect) => !effects.includes(effect))
					handlers[namespace].removeEffects(compId, effects)
				},

				toggleEffects: (...effectNames: Array<string>) => {
					const effects = invalidateComponentEffects(compId, effectNames)
					const currentEffects = activeEffectsMap[compId] || []
					activeEffectsMap[compId] = [
						...currentEffects.filter((effect) => !effects.includes(effect)),
						...effects.filter((effect) => !currentEffects.includes(effect)),
					]
					handlers[namespace].toggleEffects(compId, effects)
				},

				removeAllEffects: () => {
					activeEffectsMap[compId] = []
					handlers[namespace].removeAllEffects(compId)
				},
			}
		},
	}
}

export default {
	factory: EffectsTriggerApiFactory,
	deps: [MODELS_API, VIEWER_HANDLERS],
	name: EFFECTS_TRIGGER_API,
}
